import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CheckCircle2, XCircle,
} from 'lucide-react';
import { theme } from '../shared/theme';
import { portalFetch } from '../shared/api';
import { pollWhileActive } from '../shared/messaging';
import { useLanguage } from '../../components/LanguageContext';
import '../shared/portals.css';

// ============================================================
// AdminAssetDesk — Asset Desk, inside the app.
//
// ONE JOB: the documents waiting for approval, and the decision on each.
//
// It briefly also carried usage figures (users, logins, page views, doc
// views). Two of the four were always zero because nothing records those
// events, and none of them were the job — a screen that mixes the work with
// unrelated statistics makes the work harder to see. They are gone; the
// analytics route went back behind the admin-only gate with them.
//
// NO SECOND SIGN-IN. This tab used to ask for the shared admin password
// after the person had already signed in as themselves. Its three endpoints
// now accept an allow-listed admin session as well as the admin JWT
// (requireAdminOrPmAdmin in server.js), so the app session is enough.
// ============================================================

const POLL_MS = 120000;

// portalFetch already attaches the signed-in session, and that is now what
// these routes accept — so there is no header to add and no token to hold.
const adminAuth = (): Record<string, string> => ({});

interface PropertyDoc {
  id?: string;
  documentId?: string;
  name?: string;
  fileName?: string;
  documentType?: string;
  unitName?: string;
  investorEmail?: string;
  verificationStatus?: string;
  uploadedAt?: string;
}

function docTone(status?: string): { bg: string; fg: string } {
  const s = (status || '').toLowerCase();
  if (s === 'verified') return { bg: theme.successBg, fg: theme.success };
  if (s === 'rejected') return { bg: theme.dangerBg, fg: theme.danger };
  if (s.includes('mismatch') || s.includes('wrong_account')) return { bg: theme.warningBg, fg: theme.warning };
  return { bg: theme.infoBg, fg: theme.info };
}

const docLabel = (s?: string) => (s || 'pending').replace(/_/g, ' ');

interface Props {
  /** Recorded against a verify/reject, so a decision has a name on it. */
  staffEmail?: string;
}

const AdminAssetDesk: React.FC<Props> = ({ staffEmail }) => {
  const { t } = useLanguage();
  const [docs, setDocs] = useState<PropertyDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (!opts.silent) setLoading(true);
    try {
      const res = await portalFetch<PropertyDoc[]>('/property-documents/all', { headers: adminAuth() });
      setDocs(Array.isArray(res) ? res : []);
      setError(null);
    } catch (e: any) {
      // A rejection here means the account is not on the admin allow-list
      // (or the session has expired and the whole app will say so) — not
      // something a second password would fix.
      if (!opts.silent) setError(e?.message || t('adLoadFailed'));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [t]);

  // Verifying and rejecting are the job this queue exists for, so they happen
  // here rather than in another window. Optimistic, and put back if the
  // server refuses — a document that silently stayed pending after being
  // marked verified would be worse than an error.
  // The screen is a QUEUE, so it opens on what is waiting. Listing all 83
  // documents by default — every one of them already verified — is a list of
  // work that is finished, which is the opposite of a queue. The rest stay
  // one tap away, because looking up a specific document is a real thing to
  // want; it is just not what this screen is for.
  const [filter, setFilter] = useState<'waiting' | 'verified' | 'rejected' | 'all'>('waiting');
  const [busyDoc, setBusyDoc] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const decide = useCallback(async (docId: string, verdict: 'verify' | 'reject', why?: string) => {
    const before = docs;
    setBusyDoc(docId);
    setDocs((prev) => prev.map((d) => ((d.id || d.documentId) === docId
      ? { ...d, verificationStatus: verdict === 'verify' ? 'verified' : 'rejected' }
      : d)));
    try {
      await portalFetch(`/property-documents/${verdict}`, {
        method: 'POST',
        headers: adminAuth(),
        body: JSON.stringify(verdict === 'verify'
          ? { documentId: docId, verifiedBy: staffEmail || 'admin' }
          : { documentId: docId, reason: why || '', rejectedBy: staffEmail || 'admin' }),
      });
      setError(null);
      setRejecting(null);
      setReason('');
    } catch (e: any) {
      setDocs(before);
      setError(e?.message || t('adDecisionFailed'));
    } finally {
      setBusyDoc(null);
    }
  }, [docs, staffEmail, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => pollWhileActive(() => load({ silent: true }), POLL_MS), [load]);

  // "Waiting" is anything not yet decided — pending_verification, but also
  // name_mismatch_pending, wrong_account_pending and
  // awaiting_investor_confirmation, which are all still someone's problem.
  const isWaiting = (d: PropertyDoc) => {
    const st = (d.verificationStatus || '').toLowerCase();
    return st !== 'verified' && st !== 'rejected';
  };
  const counts = {
    waiting: docs.filter(isWaiting).length,
    verified: docs.filter((d) => (d.verificationStatus || '').toLowerCase() === 'verified').length,
    rejected: docs.filter((d) => (d.verificationStatus || '').toLowerCase() === 'rejected').length,
    all: docs.length,
  };
  const shown = filter === 'all' ? docs
    : filter === 'waiting' ? docs.filter(isWaiting)
      : docs.filter((d) => (d.verificationStatus || '').toLowerCase() === filter);

  return (
    <div className="pt-fade" style={{ padding: 14 }}>


      {error && <div className="pm-chat-error" style={{ margin: '0 0 10px' }}>{error}</div>}

      <div style={{ display: 'flex', gap: 7, marginBottom: 12, flexWrap: 'wrap' }}>
        {([['waiting', t('adFilterWaiting'), counts.waiting],
           ['verified', t('adFilterVerified'), counts.verified],
           ['rejected', t('adFilterRejected'), counts.rejected],
           ['all', t('inbFilterAll'), counts.all]] as const).map(([k, label, n]) => {
          const on = filter === k;
          return (
            <button
              key={k}
              onClick={() => setFilter(k as typeof filter)}
              className="pt-tap"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: on ? theme.navy : '#fff',
                color: on ? '#fff' : theme.slate,
                border: `1px solid ${on ? theme.navy : theme.line}`,
                borderRadius: 99, padding: '7px 13px', minHeight: 0,
                fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              }}
            >
              {label} <span style={{ opacity: 0.75 }}>{n}</span>
            </button>
          );
        })}
      </div>

      {loading && !docs.length && (
        <>
          <div className="pt-skel" style={{ height: 66, marginBottom: 8 }} />
          <div className="pt-skel" style={{ height: 66, marginBottom: 8 }} />
          <div className="pt-skel" style={{ height: 66 }} />
        </>
      )}

      {!loading && !shown.length && (
        <div className="pm-chat-empty">
          <CheckCircle2 size={30} color={theme.gold} />
          <div className="pm-chat-empty-title">
            {filter === 'waiting' ? t('adNothingWaiting') : t('adNoneHere')}
          </div>
          <div className="pm-chat-empty-text">
            {filter === 'waiting' ? t('adAllClearText') : t('adNothingWaitingText')}
          </div>
        </div>
      )}

      {shown.map((d, i) => {
        const tone = docTone(d.verificationStatus);
        const docId = d.id || d.documentId || '';
        const st = (d.verificationStatus || '').toLowerCase();
        return (
          <div key={d.id || d.documentId || i} className="pm-issue-row" style={{ borderLeftColor: tone.fg }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="pm-issue-unit">{d.name || d.fileName || d.documentType || '—'}</div>
              <div className="pm-issue-meta">
                {[d.unitName, d.documentType, d.investorEmail].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
            <div className="pm-issue-side">
              <span className="pm-issue-status" style={{ background: tone.bg, color: tone.fg }}>
                {docLabel(d.verificationStatus)}
              </span>
              {/* THE CONSOLE'S ACTION MATRIX, matched exactly (admin.html):
                  anything undecided can be verified or rejected, a verified
                  document can be revoked, a rejected one can still be
                  verified. Every state has its action.

                  This previously keyed off `.includes('pending')`, which is
                  why the one document actually waiting —
                  awaiting_investor_confirmation — listed under "Awaiting
                  review" with no way to act on it. The filter said it was
                  waiting and the row disagreed. */}
              {docId && rejecting !== docId && (
                <div style={{ display: 'flex', gap: 5 }}>
                  {st !== 'verified' && (
                    <button onClick={() => decide(docId, 'verify')} disabled={busyDoc === docId} className="pt-tap pm-issue-act is-done">
                      <CheckCircle2 size={12} /> {t('adVerify')}
                    </button>
                  )}
                  {st !== 'rejected' && (
                    <button onClick={() => { setRejecting(docId); setReason(''); }} disabled={busyDoc === docId} className="pt-tap pm-issue-act is-reject">
                      <XCircle size={12} /> {st === 'verified' ? t('adRevoke') : t('adReject')}
                    </button>
                  )}
                </div>
              )}
              {/* A rejection tells an investor their document was refused, so
                  it carries a reason rather than happening on one tap. */}
              {docId && rejecting === docId && (
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && reason.trim()) decide(docId, 'reject', reason.trim()); }}
                    placeholder={t('adRejectReason')}
                    autoFocus
                    style={{ width: 168, border: `1px solid ${theme.line}`, borderRadius: 8, padding: '5px 9px', fontSize: 12 }}
                  />
                  <button onClick={() => reason.trim() && decide(docId, 'reject', reason.trim())} disabled={!reason.trim() || busyDoc === docId} className="pt-tap pm-issue-act is-reject">
                    {t('adConfirm')}
                  </button>
                  <button onClick={() => setRejecting(null)} className="pt-tap pm-issue-act">{t('inbCancel')}</button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AdminAssetDesk;
